import { useEffect } from "react";
import { ModalProps } from "../../interfaces/modal";
import { useSocialStore } from "../../stores/social";
import { getUsers, sendAccess } from "../../api/users";
import { showToastError, showToastSuccess } from "../../services/toast";
import {
  createContactSocial,
  getCollabAssignByCustomer,
  getUsersAssignByCustomer,
  desassignCollaborators,
  deleteUser,
  postMonth,
  assignCollaboratorsSocial,
} from "../../api/social";
import send from "../../assets/icons/darkIcons/send.svg";
import trash from "../../assets/icons/darkIcons/trash.svg";
import { Select } from "../ui/inputs/Select";
import { months } from "../../data/social";
import { getOneCustomerFile } from "../../api/customerFile";
import { Input } from "../ui/inputs/Input";
import { ModalButton, ModalShell } from "../ui/modals/ModalShell";

const Assignation: React.FC<ModalProps> = ({ closeModal, idCustomerFile }) => {
  const {
    collaborators,
    setCollaborators,
    checkboxValuesCollabs,
    setCheckboxValuesCollabs,
    socialContacts,
    setSocialContacts,
    updateSocial,
    setUpdateSocial,
    resetSocial,
    formData,
    setFormData,
  } = useSocialStore();

  useEffect(() => {
    // Récupere la liste de collab de Company uniquement social
    getUsers(true)
      .then((res) => {
        const collaboratorsSocial = res.data.filter(
          (user: any) => user.departments && user.departments.includes("SOCIAL")
        );
        setCollaborators(collaboratorsSocial);
      })
      .catch(() => {
        showToastError("Erreur lors de la récupération des collaborateurs");
      });

    // Récupere les collaborateurs assignés au dossier
    getCollabAssignByCustomer(Number(idCustomerFile))
      .then((res) => {
        // Créer un objet avec les IDs des collaborateurs assignés
        const assignedCollabs = res.data.reduce((acc: any, collab: any) => {
          acc[collab.id] = true;
          return acc;
        }, {});

        setCheckboxValuesCollabs(assignedCollabs);
      })
      .catch(() => {
        showToastError(
          "Erreur lors de la récupération des collaborateurs assignés"
        );
      });

    // Récupere les users assigne au dossier
    getUsersAssignByCustomer(Number(idCustomerFile))
      .then((res) => {
        setSocialContacts(res.data);
      })
      .catch(() => {
        showToastError("Erreur lors de la récupération des contacts sociaux");
      });

    // Récupère le dossier client
    getOneCustomerFile(Number(idCustomerFile))
      .then((res) => {
        const match = months.find(
          (month: any) => month.value == res?.data?.sc_Month
        );
        setFormData({
          sc_Month: match?.value || "",
          sc_UnitPrice: res?.data?.sc_UnitPrice || "",
          label: match?.label || "",
        });
      })
      .catch(() => {
        showToastError("Une erreur est survenue");
      });
  }, [idCustomerFile, updateSocial]);

  const handleAddInterlocuteur = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    setSocialContacts([
      ...socialContacts,
      {
        email: "",
        bulletin: false,
        id: undefined,
        lastConnection: null,
      }, // Ajout d'un nouvel objet vide
    ]);
  };

  const handleCheckboxChangeCollabs = (value: number, checked: boolean) => {
    if (!checked) {
      desassignCollaborators(Number(idCustomerFile), value)
        .then(() => {
          showToastSuccess("Utilisateur désassigné.");
        })
        .catch(() => {
          showToastError("Une erreur est survenue.");
        });
    } else {
      assignCollaboratorsSocial(Number(idCustomerFile), Number(value))
        .then(() => {
          showToastSuccess("Utilisateur assigné.");
        })
        .catch(() => {
          showToastError("Une erreur est survenue.");
        });
    }
    setCheckboxValuesCollabs({ ...checkboxValuesCollabs, [value]: checked });
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: parseInt(e.target.value) });
  };

  const handleChangeNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: parseInt(e.target.value) });
  };

  // Fonction pour modifier un champ email
  const handleEmailChange = (index: number, value: string) => {
    const updatedInterlocuteurs = [...socialContacts];
    updatedInterlocuteurs[index].email = value;
    setSocialContacts(updatedInterlocuteurs);
  };

  // Fonction pour modifier un champ checkbox
  const handleCheckboxChange = (index: number) => {
    const updatedInterlocuteurs = [...socialContacts];
    updatedInterlocuteurs[index].bulletin =
      !updatedInterlocuteurs[index].bulletin;
    setSocialContacts(updatedInterlocuteurs);
  };

  const handleSendAccess = (id: number) => {
    sendAccess(id)
      .then(() => {
        showToastSuccess("Accès renvoyé");
      })
      .catch(() => {
        showToastError("Une erreur est survenue");
      });
  };

  const deleteInterlocuteur = (id: number | undefined, index: number) => {
    if (!id) {
      // Si l'interlocuteur n'a pas d'ID, on l'enlève juste du state local en utilisant l'index
      setSocialContacts(
        socialContacts.filter((_, i) => i !== index)
      );
      return;
    }

    deleteUser(Number(idCustomerFile), id)
      .then(() => {
        showToastSuccess("Interlocuteur supprimé");
        setUpdateSocial(!updateSocial);
      })
      .catch(() => {
        showToastError("Une erreur est survenue");
      });
  };

  // Création et assignation d un utilisateur + assignation users Company
  const handleCreateUserAndAssign = () => {
    const promises = [];
    if (formData.sc_Month === "") {
      showToastError("Veuillez sélectionner un mois");
      return;
    }
    // Ajouter les promesses pour les contacts sociaux
    if (socialContacts?.length > 0) {
      promises.push(
        createContactSocial(Number(idCustomerFile), socialContacts)
      );
    }

    if (formData.sc_Month) {
      promises.push(postMonth(Number(idCustomerFile), parseInt(formData.sc_Month), parseInt(formData.sc_UnitPrice)));
    }

    Promise.all(promises)
      .then(() => {
        closeModal();
        resetSocial();
        setUpdateSocial(!updateSocial);
        showToastSuccess(
          "Tous les utilisateurs ont été créés et assignés avec succès"
        );
      })
      .catch(() => {
        showToastError(
          "Une erreur est survenue lors de la création des utilisateurs"
        );
      });
  };

  return (
    <ModalShell
      title="Assignations"
      size="full"
      onClose={() => {
        closeModal();
        resetSocial();
      }}
      footer={
        <>
          <ModalButton
            variant="secondary"
            onClick={() => {
              closeModal();
              resetSocial();
            }}
          >
            Annuler
          </ModalButton>
          <ModalButton variant="primary" onClick={handleCreateUserAndAssign}>
            Assigner
          </ModalButton>
        </>
      }
    >
      {/* Section Mois en cours */}
      <div className="grid grid-cols-1 gap-3 font-Nunito sm:grid-cols-2 lg:max-w-2xl">
        <Select
          label="Mois en cours"
          name="sc_Month"
          required
          value={formData?.sc_Month?.toString() || ""}
          onChange={handleSelectChange}
          fetchOptions={months}
          className="w-full"
        />

        <Input
          label="Prix unitaire"
          name="sc_UnitPrice"
          value={formData?.sc_UnitPrice || ""}
          onChange={handleChangeNumber}
          clasName="w-full"
        />
      </div>

      {/* Section Assignations */}
      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Collaborateurs affectés */}
        <section className="min-w-0">
          <h3 className="mb-2 text-[11px] font-bold tracking-wider text-slate-400 uppercase">
            Collaborateurs affectés
          </h3>
          <div className="max-h-72 overflow-y-auto rounded-xl border border-slate-200">
            <div className="sticky top-0 flex border-b border-slate-100 bg-slate-50 px-4 py-2.5">
              <div className="w-1/2 text-[11px] font-bold tracking-wider text-slate-500 uppercase">
                Prénom
              </div>
              <div className="w-1/2 text-[11px] font-bold tracking-wider text-slate-500 uppercase">
                Nom
              </div>
            </div>
            <div className="divide-y divide-slate-100">
              {collaborators?.map((user) => (
                <div
                  key={user?.id}
                  className="flex items-center px-4 py-2.5 transition-colors hover:bg-slate-50"
                >
                  <div className="flex w-1/2 items-center gap-2.5">
                    <input
                      type="checkbox"
                      checked={checkboxValuesCollabs[user?.id] || false}
                      onChange={() => {
                        handleCheckboxChangeCollabs(
                          user?.id,
                          !checkboxValuesCollabs[user?.id]
                        );
                      }}
                      className="size-4 shrink-0 cursor-pointer accent-dca-blue"
                    />
                    <span className="truncate font-Nunito text-sm text-slate-700">
                      {user?.firstName}
                    </span>
                  </div>
                  <div className="w-1/2 truncate font-Nunito text-sm text-slate-700">
                    {user?.lastName}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Clients affectés */}
        <section className="min-w-0">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-[11px] font-bold tracking-wider text-slate-400 uppercase">
              Clients affectés
            </h3>
            <button
              type="button"
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-xl bg-indigo-50 px-3 py-1.5 font-Nunito text-xs font-semibold text-indigo-700 ring-1 ring-indigo-100 ring-inset transition-colors hover:bg-indigo-100"
              onClick={(e) => handleAddInterlocuteur(e)}
            >
              Ajouter un interlocuteur
            </button>
          </div>
          <div className="max-h-72 overflow-y-auto rounded-xl border border-slate-200">
            <div className="sticky top-0 hidden border-b border-slate-100 bg-slate-50 px-4 py-2.5 sm:flex">
              <div className="w-2/5 text-[11px] font-bold tracking-wider text-slate-500 uppercase">
                Email
              </div>
              <div className="w-1/5 text-[11px] font-bold tracking-wider text-slate-500 uppercase">
                Bulletin
              </div>
              <div className="w-1/5 text-[11px] font-bold tracking-wider text-slate-500 uppercase">
                Accès
              </div>
              <div className="w-1/5 text-[11px] font-bold tracking-wider text-slate-500 uppercase">
                Actions
              </div>
            </div>
            <div className="divide-y divide-slate-100">
              {socialContacts?.map((interlocuteur, i) => (
                <div
                  key={i}
                  className="flex flex-col gap-2 px-4 py-2.5 transition-colors hover:bg-slate-50 sm:flex-row sm:items-center sm:gap-0"
                >
                  <div className="min-w-0 sm:w-2/5 sm:pr-3">
                    <input
                      type="text"
                      value={interlocuteur.email}
                      onChange={(e) => handleEmailChange(i, e.target.value)}
                      placeholder="Saisissez l'email"
                      className="h-9 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 font-Nunito text-sm text-slate-800 transition-colors placeholder:text-slate-400 focus:border-indigo-300 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:outline-none"
                    />
                  </div>
                  <div className="flex items-center gap-2 sm:w-1/5">
                    <span className="text-[11px] font-bold tracking-wider text-slate-400 uppercase sm:hidden">
                      Bulletin
                    </span>
                    <input
                      type="checkbox"
                      name={`bulletin-${i}`}
                      id={`bulletin-${i}`}
                      checked={interlocuteur.bulletin}
                      className="size-4 cursor-pointer accent-dca-blue"
                      onChange={() => handleCheckboxChange(i)}
                    />
                  </div>
                  <div className="flex items-center gap-1 sm:w-2/5">
                    <button
                      type="button"
                      className="inline-flex size-9 cursor-pointer items-center justify-center rounded-xl text-slate-400 transition-colors hover:bg-indigo-50 hover:text-dca-blue"
                      onClick={(e) => {
                        e.preventDefault();
                        handleSendAccess(Number(interlocuteur.id));
                      }}
                      title="Envoyer l'accès"
                    >
                      <img src={send} alt="envoyer l'accès" className="size-4" />
                    </button>
                    <button
                      type="button"
                      className="inline-flex size-9 cursor-pointer items-center justify-center rounded-xl text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                      onClick={(e) => {
                        e.preventDefault();
                        deleteInterlocuteur(interlocuteur.id, i);
                      }}
                      title="Supprimer"
                    >
                      <img src={trash} alt="poubelle" className="size-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </ModalShell>
  );
};

export default Assignation;
